/** B2 — IVA repercutido por trimestre y tipo. Resumen orientativo: NO es el modelo 303. */
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import { Note, rateLabel, td, tdR, th, thR } from './common'
import type { VatSummaryResponse } from '@toga/shared'

export default function VatTab({ issuerId, year }: { issuerId: string; year: number }) {
  const [data, setData] = useState<VatSummaryResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setData(null)
    setError(null)
    const p = new URLSearchParams({ year: String(year) })
    if (issuerId) p.set('issuerId', issuerId)
    api
      .get<VatSummaryResponse>(`/accounting/vat-summary?${p}`)
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Error al cargar'))
    return () => {
      alive = false
    }
  }, [issuerId, year])

  if (error) return <div className="p-5 text-xs text-rose-300">{error}</div>
  if (!data) {
    return (
      <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    )
  }

  const rates = [...new Set(data.total.byRate.map((v) => v.rate))].sort(
    (a, b) => Number(b) - Number(a)
  )
  const cell = (byRate: { rate: string; baseCents: number; vatCents: number }[], rate: string) =>
    byRate.find((v) => v.rate === rate)

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-base">
              <th className={th}>Periodo</th>
              <th className={thR}>Facturas</th>
              {rates.map((r) => (
                <th key={r} className={thR}>
                  Base {rateLabel(r)}
                </th>
              ))}
              {rates
                .filter((r) => Number(r) > 0)
                .map((r) => (
                  <th key={`c${r}`} className={thR}>
                    Cuota {rateLabel(r)}
                  </th>
                ))}
              <th className={thR}>IVA repercutido</th>
              <th className={thR}>Suplidos</th>
              <th className={thR}>Retenciones IRPF</th>
            </tr>
          </thead>
          <tbody>
            {data.quarters.map((q) => (
              <tr key={q.quarter} className="border-b border-base/50">
                <td className={td}>{q.quarter}.º trimestre</td>
                <td className={tdR}>{q.invoiceCount}</td>
                {rates.map((r) => (
                  <td key={r} className={tdR}>
                    {formatCents(cell(q.byRate, r)?.baseCents ?? 0)}
                  </td>
                ))}
                {rates
                  .filter((r) => Number(r) > 0)
                  .map((r) => (
                    <td key={`c${r}`} className={tdR}>
                      {formatCents(cell(q.byRate, r)?.vatCents ?? 0)}
                    </td>
                  ))}
                <td className={`${tdR} font-semibold`}>{formatCents(q.vatCents)}</td>
                <td className={tdR}>{formatCents(q.suppliedCents)}</td>
                <td className={tdR}>{formatCents(q.irpfCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-base font-semibold">
              <td className={td}>{year}</td>
              <td className={tdR}>{data.total.invoiceCount}</td>
              {rates.map((r) => (
                <td key={r} className={tdR}>
                  {formatCents(cell(data.total.byRate, r)?.baseCents ?? 0)}
                </td>
              ))}
              {rates
                .filter((r) => Number(r) > 0)
                .map((r) => (
                  <td key={`c${r}`} className={tdR}>
                    {formatCents(cell(data.total.byRate, r)?.vatCents ?? 0)}
                  </td>
                ))}
              <td className={tdR}>{formatCents(data.total.vatCents)}</td>
              <td className={tdR}>{formatCents(data.total.suppliedCents)}</td>
              <td className={tdR}>{formatCents(data.total.irpfCents)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Note>
        Resumen orientativo del IVA que has repercutido, por trimestre natural de la fecha de
        emisión. Solo cuentan las facturas que cuentan (no las anuladas ni las sustituidas) y los
        suplidos quedan fuera de la base. No es el modelo 303: no incluye el IVA soportado ni los
        ajustes. Pásalo a tu gestoría.
      </Note>
    </div>
  )
}
